import { Link, useNavigate } from "react-router";
import { ArrowRight, FileChartColumnIncreasing } from "lucide-react";
import { Card } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { RecordOwnerDisplay } from "@/domains/record-owners/components";
import { CreateReportDialog } from "@/features/reports";
import { cn, formatRelativeTime } from "@/shared/lib/utils";
import type { DashboardReportItem } from "../queries/dashboard";
import type { DashboardSectionView } from "../hooks/use-dashboard-view-preferences";
import { DashboardCarousel } from "./dashboard-carousel";
import { DashboardSectionViewToggle } from "./dashboard-section-view-toggle";

type DashboardReportsSectionProps = {
  items: DashboardReportItem[];
  view: DashboardSectionView;
  onViewChange: (value: DashboardSectionView) => void;
};

function statusLabel(status: DashboardReportItem["status"]) {
  switch (status) {
    case "completed":
      return "Complete";
    case "in progress":
      return "Generating";
    case "pending":
    default:
      return "Pending";
  }
}

function statusClassName(status: DashboardReportItem["status"]) {
  switch (status) {
    case "completed":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "in progress":
      return "border-blue-200 bg-blue-50 text-blue-700";
    case "pending":
    default:
      return "border-amber-200 bg-amber-50 text-amber-700";
  }
}

export function DashboardReportsSection({
  items,
  view,
  onViewChange,
}: DashboardReportsSectionProps) {
  const navigate = useNavigate();

  return (
    <Card className="overflow-hidden border-border/60 bg-card/95 shadow-xs">
      <div className="flex flex-col gap-4 border-b border-border/70 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">
            Reports
          </h2>
          <p className="text-sm text-muted-foreground">Most recently updated</p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <DashboardSectionViewToggle value={view} onChange={onViewChange} />
          <CreateReportDialog
            trigger={
              <Button className="rounded-xl px-4">
                <FileChartColumnIncreasing className="size-4" />
                Run report
              </Button>
            }
          />
          <Button
            asChild
            variant="ghost"
            className="rounded-xl px-2 text-primary"
          >
            <Link to="/reports">
              View all
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-start gap-4 px-6 py-8">
          <div className="rounded-2xl border border-dashed border-border/80 bg-muted/20 p-4 text-muted-foreground">
            No recent reports yet.
          </div>
          <CreateReportDialog
            trigger={
              <Button className="rounded-xl px-4">
                <FileChartColumnIncreasing className="size-4" />
                Run report
              </Button>
            }
          />
        </div>
      ) : view === "table" ? (
        <div className="overflow-x-auto px-3 py-2">
          <table className="min-w-full border-separate border-spacing-y-2">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Updated</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Created by</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="cursor-pointer rounded-2xl border border-border/60 bg-background transition-colors hover:bg-muted/30"
                  onClick={() => void navigate(`/reports/${item.id}`)}
                >
                  <td className="rounded-l-2xl px-3 py-3 text-sm font-medium text-foreground">
                    {item.name}
                  </td>
                  <td className="px-3 py-3 text-sm text-muted-foreground">
                    <Badge variant="outline" className="rounded-full">
                      {item.type}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-sm text-muted-foreground">
                    {formatRelativeTime(item.updatedAt)}
                  </td>
                  <td className="px-3 py-3 text-sm">
                    <span
                      className={cn(
                        "inline-flex rounded-full border px-2.5 py-1 text-xs font-medium",
                        statusClassName(item.status),
                      )}
                    >
                      {statusLabel(item.status)}
                    </span>
                  </td>
                  <td className="rounded-r-2xl px-3 py-3">
                    <RecordOwnerDisplay owner={item.owner} compact />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <DashboardCarousel label="Reports">
          {items.map((item) => (
            <Card
              key={item.id}
              className="flex min-h-56 min-w-0 flex-col justify-between rounded-3xl border-border/70 bg-linear-to-br from-background to-muted/30 p-5"
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="line-clamp-2 text-lg font-semibold text-foreground">
                      {item.name}
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {item.type} • {formatRelativeTime(item.updatedAt)}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "inline-flex rounded-full border px-2.5 py-1 text-xs font-medium",
                      statusClassName(item.status),
                    )}
                  >
                    {statusLabel(item.status)}
                  </span>
                </div>
                <RecordOwnerDisplay owner={item.owner} />
              </div>

              <Button
                className="mt-6 w-full rounded-xl"
                onClick={() => void navigate(`/reports/${item.id}`)}
              >
                Open
              </Button>
            </Card>
          ))}
        </DashboardCarousel>
      )}
    </Card>
  );
}
